import { User } from "firebase/auth";
import React, { createContext, useState, ReactNode, useContext, useEffect } from "react";
import { useSnackbar } from "./shared/SnackbarProvider";
import { Enrolment } from "./shared/Enrolment";

import { db } from "./firebase-config";
import { collection, getDocs, getDoc, doc, query, where, onSnapshot, WhereFilterOp } from "firebase/firestore";
import teacherStubData from "./components/sample-teacher-data.json";
import parentStubData from "./sample-child.json";
import parentStubData2 from "./sample-child-frodo.json";

interface AppState {
  childrenData: any[]; setChildrenData: React.Dispatch<React.SetStateAction<any[]>>;
  user: User; setUser: React.Dispatch<React.SetStateAction<User>>;
  userContext: UserContext;
  loading: boolean;
  observationData: any[]; setObservationData: React.Dispatch<React.SetStateAction<any[]>>;
  portalType: 'teacher' | 'parent';
}

export class UserContext {
  constructor(private user: User) { }

  public isAdmin() {
    return this.user.email === "info@bee-happy.club";
  }

  // admin is also a teacher
  public isTeacher() {
    return this.user.email.endsWith("@bee-happy.club");
  }

  public isParent() {
    return !this.user.email.endsWith("@bee-happy.club");
  }

}

export const AppContext = createContext<AppState>({} as AppState);

export const AppProvider: React.FC<{ portalType: 'teacher' | 'parent'; children: ReactNode }> = ({
  portalType, children
}) => {

  const [childrenData, setChildrenData] = useState<any[]>();
  const [observationData, setObservationData] = useState<any[]>();
  const [user, setUser] = useState<User>();
  const [userContext, setUserContext] = useState<UserContext>();
  const [loading, setLoading] = useState<boolean>();
  const snack = useSnackbar();

  useEffect(() => {

    if (!user) {
      setChildrenData(null);
      setObservationData(null);
      setUserContext(null);
      console.log("No user");
      return;
    }

    setUserContext(new UserContext(user));

    console.log("User changed", user);

    // Use stub data in development mode
    if (import.meta.env.DEV) {
      console.log("🔧 Development mode: Using stub data");
      loadStubData();
      return;
    }

    if (portalType === 'parent') {
      getSingleChildData();
    }
    else if (portalType === 'teacher') {
      getAllData();
    }
  }, [user]);

  const loadStubData = () => {
    setLoading(true);
    
    try {
      if (portalType === 'teacher') {
        // Shared news for all children in teacher mode
        const sharedNews = [
          {
            id: "news_4",
            timeStamp: "2024-11-29T14:00:00.000Z",
            title: "New Friendships Blossoming!",
            content: "What a wonderful day at preschool! Jean-Luc and Frodo had a fantastic time playing together in our play area. They built an amazing castle with blocks and created their own adventure story. It's heartwarming to see such strong friendships developing. Great teamwork, boys!",
            date: "2024-11-29T00:00:00.000Z",
            photoFileUrl: "/stub_news.jpeg",
            photoUrl: "/stub_news.jpeg",
            editUrl: "https://docs.google.com/forms/d/e/1FAIpQLSf38bycyfFBHzGX_TQkFS7u4zHD--KSTf7HsUQISAf_jLBD_w/viewform?edit2=2_sample4",
            ignore: false
          },
          {
            id: "news_1",
            timeStamp: "2024-11-20T09:00:00.000Z",
            title: "Upcoming Christmas Nativity Play",
            content: "We're excited to announce our Christmas Nativity Play will be held on December 15th at 2:00 PM. All families are welcome to attend. The children have been practicing their songs and lines, and we can't wait to share their performance with you!",
            date: "2024-12-15T14:00:00.000Z",
            photoFileUrl: "https://images.unsplash.com/photo-1512389142860-9c449e58a543",
            photoUrl: "https://images.unsplash.com/photo-1512389142860-9c449e58a543",
            editUrl: "https://docs.google.com/forms/d/e/1FAIpQLSf38bycyfFBHzGX_TQkFS7u4zHD--KSTf7HsUQISAf_jLBD_w/viewform?edit2=2_sample",
            ignore: false
          },
          {
            id: "news_2",
            timeStamp: "2024-11-28T10:30:00.000Z",
            title: "Winter Walk in Hedgerley Woods",
            content: "Today we enjoyed a wonderful winter walk in Hedgerley Woods! The children loved exploring nature, collecting leaves, and spotting birds. We talked about the changing seasons and enjoyed hot chocolate back at preschool. What a fantastic outdoor learning experience!",
            date: "2024-11-28T00:00:00.000Z",
            photoFileUrl: "https://images.unsplash.com/photo-1511593358241-7eea1f3c84e5",
            photoUrl: "https://images.unsplash.com/photo-1511593358241-7eea1f3c84e5",
            editUrl: "https://docs.google.com/forms/d/e/1FAIpQLSf38bycyfFBHzGX_TQkFS7u4zHD--KSTf7HsUQISAf_jLBD_w/viewform?edit2=2_sample2",
            ignore: false
          },
          {
            id: "news_3",
            timeStamp: "2024-11-15T11:00:00.000Z",
            title: "Bee Happy Harvest Festival",
            content: "Thank you to all families who contributed to our Harvest Festival! We collected an amazing amount of food donations which have been donated to the local food bank. The children learned about helping others in our community and the importance of sharing.",
            date: "2024-11-15T00:00:00.000Z",
            photoFileUrl: "https://images.unsplash.com/photo-1464454709131-ffd692591ee5",
            photoUrl: "https://images.unsplash.com/photo-1464454709131-ffd692591ee5",
            editUrl: "https://docs.google.com/forms/d/e/1FAIpQLSf38bycyfFBHzGX_TQkFS7u4zHD--KSTf7HsUQISAf_jLBD_w/viewform?edit2=2_sample3",
            ignore: false
          }
        ];
        
        // Process teacher stub data
        const processedData = (teacherStubData as any[]).map((child: any) => ({
          ...child,
          id: child.childId,
          photoId: child.avatarPhoto || 'sample', // Use avatarPhoto from stub data
          registration: {
            id: child.registrationId || `reg_${child.childId}`,
            email: child.email,
            startDate: child.startDate,
            childFirstName: child.childFirstName,
            childMiddleName: child.childMiddleName || '',
            childLastName: child.childLastName || child.childId.split('_')[1],
            childDateOfBirth: child.childDOB,
            childSex: child.childSex || 'Boy',
            childFoodPref: child.childFoodPref || 'None',
            childAllergens: child.childAllergens || 'None',
            childHealth: child.childHealth || 'None',
            childOtherDetails: child.childOtherDetails || 'None',
            parent1FullName: child.parent1FullName || child.parent1Name,
            parent1Address: child.parent1Address,
            parent1Phone: child.parent1Phone,
            parent1Email: child.parent1Email,
            parent2FullName: child.parent2FullName || child.parent2Name,
            parent2Address: child.parent2Address,
            parent2Phone: child.parent2Phone,
            parent2Email: child.parent2Email,
            emergencyContactFullName: child.emergencyContactFullName || 'N/A',
            emergencyContactRelationship: child.emergencyContactRelationship || 'N/A',
            emergencyContactAddress: child.emergencyContactAddress || 'N/A',
            emergencyContactPhone: child.emergencyContactPhone || 'N/A',
            emergencyContactEmail: child.emergencyContactEmail || 'N/A',
            otherCollectorFullName: child.otherCollectorFullName || 'N/A',
            otherCollectorRelationship: child.otherCollectorRelationship || 'N/A',
            otherCollectorPhone: child.otherCollectorPhone || 'N/A',
            otherCollectorCodeWord: child.otherCollectorCodeWord || 'N/A',
            doctorFullName: child.doctorFullName || 'N/A',
            doctorPractice: child.doctorPractice || 'N/A',
            doctorAddress: child.doctorAddress || 'N/A',
            doctorPhone: child.doctorPhone || 'N/A',
            healthVisitorFullName: child.healthVisitorFullName || 'N/A',
            healthVisitorOrganisation: child.healthVisitorOrganisation || 'N/A',
            healthVisitorAddress: child.healthVisitorAddress || 'N/A',
            healthVisitorPhone: child.healthVisitorPhone || 'N/A',
            socialCareWorkerFullName: child.socialCareWorkerFullName || 'N/A',
            socialCareWorkerOrganisation: child.socialCareWorkerOrganisation || 'N/A',
            socialCareWorkerAddress: child.socialCareWorkerAddress || 'N/A',
            socialCareWorkerPhone: child.socialCareWorkerPhone || 'N/A',
            fundingEntitlement: child.fundingEntitlement || 'N/A',
            reliefSchemesInfo: child.reliefSchemesInfo || 'N/A',
            photoConsent: child.photoConsent || 'N/A',
            enrolment: new Enrolment(child.enrolmentRaw || ""),
            ignore: false,
          },
          observations: child.observations || [],
          accidents: child.accidents || [],
          news: sharedNews,
        }));
        
        setChildrenData(processedData);
        setObservationData(processedData.flatMap((c: any) => c.observations || []));
        console.log("✅ Loaded stub data for teachers:", processedData);
        snack("✅ Loaded stub data for teachers", "success");
      } else {
        // Process parent stub data - load both sample children
        const processChildData = (childData: any) => ({
          ...childData,
          id: childData.childId,
          photoId: childData.avatarPhoto || 'sample', // Use avatarPhoto from stub data
          registration: {
            id: childData.registrationId || `reg_${childData.childId}`,
            email: childData.email,
            startDate: childData.startDate,
            childFirstName: childData.childFirstName,
            childMiddleName: childData.childMiddleName || '',
            childLastName: childData.childLastName || childData.childId.split('_')[1],
            childDateOfBirth: childData.childDOB,
            childSex: childData.childSex || 'Boy',
            childFoodPref: childData.childFoodPref || 'None',
            childAllergens: childData.childAllergens || 'None',
            childHealth: childData.childHealth || 'None',
            childOtherDetails: childData.childOtherDetails || 'None',
            parent1FullName: childData.parent1FullName || childData.parent1Name,
            parent1Address: childData.parent1Address,
            parent1Phone: childData.parent1Phone,
            parent1Email: childData.parent1Email,
            parent2FullName: childData.parent2FullName || childData.parent2Name,
            parent2Address: childData.parent2Address,
            parent2Phone: childData.parent2Phone,
            parent2Email: childData.parent2Email,
            emergencyContactFullName: childData.emergencyContactFullName || 'N/A',
            emergencyContactRelationship: childData.emergencyContactRelationship || 'N/A',
            emergencyContactAddress: childData.emergencyContactAddress || 'N/A',
            emergencyContactPhone: childData.emergencyContactPhone || 'N/A',
            emergencyContactEmail: childData.emergencyContactEmail || 'N/A',
            otherCollectorFullName: childData.otherCollectorFullName || 'N/A',
            otherCollectorRelationship: childData.otherCollectorRelationship || 'N/A',
            otherCollectorPhone: childData.otherCollectorPhone || 'N/A',
            otherCollectorCodeWord: childData.otherCollectorCodeWord || 'N/A',
            doctorFullName: childData.doctorFullName || 'N/A',
            doctorPractice: childData.doctorPractice || 'N/A',
            doctorAddress: childData.doctorAddress || 'N/A',
            doctorPhone: childData.doctorPhone || 'N/A',
            healthVisitorFullName: childData.healthVisitorFullName || 'N/A',
            healthVisitorOrganisation: childData.healthVisitorOrganisation || 'N/A',
            healthVisitorAddress: childData.healthVisitorAddress || 'N/A',
            healthVisitorPhone: childData.healthVisitorPhone || 'N/A',
            socialCareWorkerFullName: childData.socialCareWorkerFullName || 'N/A',
            socialCareWorkerOrganisation: childData.socialCareWorkerOrganisation || 'N/A',
            socialCareWorkerAddress: childData.socialCareWorkerAddress || 'N/A',
            socialCareWorkerPhone: childData.socialCareWorkerPhone || 'N/A',
            fundingEntitlement: childData.fundingEntitlement || 'N/A',
            reliefSchemesInfo: childData.reliefSchemesInfo || 'N/A',
            photoConsent: childData.photoConsent || 'N/A',
            enrolment: new Enrolment(childData.enrolmentRaw || ""),
            ignore: false,
          },
          observations: childData.observations || [],
          accidents: childData.accidents || [],
          news: childData.news || [],
        });
        
        const processedChildren = [
          processChildData(parentStubData as any),
          processChildData(parentStubData2 as any)
        ];
        
        setChildrenData(processedChildren);
        console.log("✅ Loaded stub data for parents:", processedChildren);
        snack("✅ Loaded stub data for parents (2 children)", "success");
      }
    } catch (error) {
      console.error("Error loading stub data:", error);
      snack("Error loading stub data", "error");
    } finally {
      setLoading(false);
    }
  };

  const getPhotoUrl = (url: string): string | null => {
    const matches = url.match(/id=([^&]+)/);
    if (!matches) {
      return null;
    }
    return `https://drive.google.com/thumbnail?id=${matches[1]}&sz=w1000`
  }

  const getSingleChildData = async () => {
    setLoading(true);
    console.log("Getting child data for parent", user.email);
    let chData = await fetchWithFilter("children", [{ property: "parentEmail", value: user.email }], ["registrations"]);

    if (chData.length < 1) {
      setLoading(false);
      return;
    }

    let [observationReviews, accidentReviews, news] = await Promise.all([


      fetchWithFilter("observationReviews", [
        { property: "childIds", value: chData.map(c => c.id), op: "array-contains-any" }, // different op because of group observations
        { property: "reviewed", value: true },
        { property: "visibleToParents", value: true }
      ]),

      fetchWithFilter("accidentReviews", [
        { property: "childId", value: chData.map(c => c.id) },
        { property: "reviewed", value: true },
        { property: "visibleToParents", value: true }
      ]),


      fetchWithFilter("news", [
        { property: "ignore", value: false }
      ])

    ]);



    console.log("Observations and accidents reviews", { observationReviews, accidentReviews, news });

    for (const child of chData) {

      child.registration = child.registrations
        .filter((reg: any) => !reg.ignore)
        .find((reg: any) => reg.id === child.registrationId);

      let observations: any[] = [];
      if (observationReviews && observationReviews.length > 0) {
        observations = await fetchWithFilter(`children/${child.id}/observations`, [
          { property: "childId", value: child.id },
          { property: "id", value: observationReviews.map(r => r.id) },
        ])
      }

      let accidents: any[] = [];
      if (accidentReviews && accidentReviews.length > 0) {
        accidents = await fetchWithFilter(`children/${child.id}/accidents`, [
          { property: "childId", value: child.id },
          { property: "id", value: accidentReviews.map(r => r.id) },
        ])
      }

      console.log("Observations and accidents", { observations, accidents });


      child.observations = observations
        .filter((obs: any) => obs.childId.split(",").map((x: any) => x.trim()).includes(child.id));

      child.observations.forEach((obs: any) => {
        obs.photoUrl = getPhotoUrl(obs.photoFileUrl);
      });

      child.accidents = accidents.filter((acc: any) => acc.childId === child.id);

      child.registration.enrolment = new Enrolment(child.registration.enrolment);


      news.forEach((newsItem: any) => {
        newsItem.photoUrl = getPhotoUrl(newsItem.photoFileUrl);
      });

      child.news = news;
    }

    setChildrenData(chData);
    setLoading(false);
  }

  const getAllData = async () => {
    snack("Getting data...", "info");

    let [chData, regData, obsData, accData, newsData, observationReviews, accidentReviews] = await Promise.all([
      fetch("children"),
      fetch("registrations"),
      fetch("observations"),
      fetch("accidents"),
      fetchWithFilter("news", [
        { property: "ignore", value: false }
      ]),
      fetch("observationReviews"),
      fetch("accidentReviews"),
    ]);

    chData = chData.filter((ch: any) => !ch.ignore);

    obsData.forEach((obs: any) => {
      const review = observationReviews.find((rev: any) => rev.id === obs.id);
      obs.photoUrl = getPhotoUrl(obs.photoFileUrl);

      if (review) {
        obs.reviewed = review.reviewed;
        obs.visibleToParents = review.visibleToParents;
        obs.ignore = review.ignore;
      }
    });
    obsData = obsData.filter((obs: any) => !obs.ignore);

    newsData.forEach((news: any) => {
      news.photoUrl = getPhotoUrl(news.photoFileUrl);
    });

    accData.forEach((acc: any) => {
      const review = accidentReviews.find((rev: any) => rev.id === acc.id);
      if (!review) { return; }
      acc.reviewed = review.reviewed;
      acc.visibleToParents = review.visibleToParents;
    });

    chData.forEach((child: any) => {
      child.registration = regData
        .filter((reg: any) => !reg.ignore)
        .find((reg: any) => reg.id === child.registrationId);
      child.observations = obsData
        .filter((obs: any) => obs.childId.split(",").map((x: any) => x.trim()).includes(child.id));
      child.accidents = accData.filter((acc: any) => acc.childId === child.id);
      child.news = newsData;
      child.registration.enrolment = new Enrolment(child.registration.enrolment);
    });


    chData.forEach((child: any) => {
      console.log(child.id, " => ", child);
    })

    setObservationData(obsData);
    setChildrenData(chData);


    onSnapshot(collection(db, "observationReviews"), (querySnapshot) => {
      const data = querySnapshot.docs.map(doc => doc.data());
      console.log("Observation reviews updated", data);

      const updatedObsData = [...obsData];
      updatedObsData.forEach((obs: any) => {
        const review = data.find((rev: any) => rev.id === obs.id);
        if (review) {
          obs.reviewed = review.reviewed;
          obs.visibleToParents = review.visibleToParents;
        }
      });

      const updatedChData = [...chData];
      updatedChData.forEach((child: any) => {
        child.observations = updatedObsData.filter((obs: any) => obs.childId.split(",").map((x: any) => x.trim()).includes(child.id));
      });

      setObservationData(updatedObsData);
      setChildrenData([...chData]);
    });


    onSnapshot(collection(db, "accidentReviews"), (querySnapshot) => {
      const data = querySnapshot.docs.map(doc => doc.data());
      console.log("Accident reviews updated", data);

      const updatedAccData = [...accData];
      updatedAccData.forEach((acc: any) => {
        const review = data.find((rev: any) => rev.id === acc.id);
        if (review) {
          acc.reviewed = review.reviewed;
          acc.visibleToParents = review.visibleToParents;
        }
      });

      const updatedChData = [...chData];
      updatedChData.forEach((child: any) => {
        child.accidents = updatedAccData.filter((acc: any) => acc.childId === child.id);
      });

      setChildrenData([...chData]);
    });
  }

  const fetch = async (collectionName: string) => {
    try {
      console.log(`Getting ${collectionName} data`);

      const querySnapshot = await getDocs(collection(db, collectionName));

      const data: any[] = []

      querySnapshot.forEach((doc) => {
        const docData = doc.data()
        data.push(docData);
      });

      console.log(`Received ${data.length} items for ${collectionName}`);
      console.log(collectionName, data);
      return data;

    } catch (error) {
      console.error(`Error getting ${collectionName}`, error);
      snack("Error getting data", "error");
      return [];
    }
  }

  const fetchDocument = async (collectionName: string, documentId: string) => {
    try {
      console.log(`Getting document ${documentId} from ${collectionName}`);

      const docRef = doc(db, collectionName, documentId);
      const docSnapshot = await getDoc(docRef);

      if (docSnapshot.exists()) {
        const docData = docSnapshot.data();
        console.log(`Received document:`, docData);
        return docData;
      } else {
        console.log(`Document ${documentId} does not exist in ${collectionName}`);
        return null;
      }

    } catch (error) {
      console.error(`Error getting document ${documentId} from ${collectionName}`, error);
      snack("Error getting document", "error");
      return null;
    }
  };

  const fetchWithFilter = async (
    collectionName: string,
    filters?: { property: string, value: any, op?: WhereFilterOp }[],
    nestedCollectionNames?: string[],
    _disableBatching?: boolean
  ): Promise<any[]> => {
    try {
      console.log(`Getting documents from ${collectionName}`, { filters });

      // Check for batching need (only if not already batching)
      const BATCH_SIZE = 25;
      if (!_disableBatching && filters) {
        const batchFilterIdx = filters.findIndex(f => Array.isArray(f.value) && f.value.length > BATCH_SIZE);
        if (batchFilterIdx !== -1) {
          // Only batch on the first filter with array > BATCH_SIZE
          const batchFilter = filters[batchFilterIdx];
          const batches = [];
          for (let i = 0; i < batchFilter.value.length; i += BATCH_SIZE) {
            const batchValues = batchFilter.value.slice(i, i + BATCH_SIZE);
            const batchFilters = filters.map((f, idx) => idx === batchFilterIdx ? { ...f, value: batchValues } : f);
            batches.push(fetchWithFilter(collectionName, batchFilters, nestedCollectionNames, true));
          }
          // Await all batches and flatten
          const results = (await Promise.all(batches)).flat();
          return results;
        }
      }

      let q = query(collection(db, collectionName));

      if (filters) {
        filters.forEach(({ property, value, op }) => {
          const whereOp = op || (Array.isArray(value) ? "in" : "==");
          console.log("whereOp", { op, whereOp, isArr: Array.isArray(value) });
          q = query(q, where(property, whereOp, value));
        });
      }

      const querySnapshot = await getDocs(q);

      const data: any[] = [];

      for (const doc of querySnapshot.docs) {
        const docData = doc.data();

        const nestedDatas: any = {};
        if (nestedCollectionNames && nestedCollectionNames.length > 0) {
          for (const nestedCollectionName of nestedCollectionNames) {

            console.log(`Getting nested collection ${nestedCollectionName} for ${collectionName} at path ${doc.ref.path}`);

            const nestedCollectionRef = collection(doc.ref, nestedCollectionName);
            const nestedSnapshot = await getDocs(nestedCollectionRef);

            const nestedData: any[] = [];
            nestedSnapshot.forEach((nestedDoc) => {
              nestedData.push({ id: nestedDoc.id, ...nestedDoc.data() });
            });

            nestedDatas[nestedCollectionName] = nestedData;

            console.log(`Received ${nestedData.length} items for nested ${nestedCollectionName}`, { filters, nestedData });
          }

          data.push({ id: doc.id, ...docData, ...nestedDatas });
        }
        else {
          data.push({ id: doc.id, ...docData });
        }
      }

      console.log(`Received ${data.length} items for ${collectionName}`, { filters, data });
      return data;

    } catch (error) {
      console.error(`Error getting filtered data from ${collectionName}`, error);
      snack("Error getting data", "error");
      return [];
    }
  };

  return (
    <AppContext.Provider value={{
      observationData, setObservationData,
      childrenData, setChildrenData,
      user, setUser,
      userContext,
      loading,
      portalType
    }}>
      {children}
    </AppContext.Provider>
  );
};
